import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Form, Input, List, Modal, Popconfirm, Row, Select, Space, Spin, Statistic, Switch, Tabs, Tag, Timeline, Typography, Upload, message } from 'antd';
import { CalendarOutlined, DeleteOutlined, ExclamationCircleOutlined, MessageOutlined, PlusOutlined, ReloadOutlined, SmileOutlined, TrophyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import studentAPI from '../../services/studentAPI';
import './StudentGrowthRecordV2.css';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

const conversationTypes = [
  { value: 'daily', label: '日常谈话' },
  { value: 'care', label: '关爱谈话' },
  { value: 'warning', label: '提醒谈话' },
  { value: 'follow_up', label: '跟进谈话' }
];

const timelineFilters = [
  { value: 'all', label: '全部' },
  { value: 'praise', label: '表扬' },
  { value: 'violation', label: '违纪' },
  { value: 'points', label: '积分' },
  { value: 'homework', label: '作业' },
  { value: 'grade', label: '成绩' },
  { value: 'conversation', label: '谈话' },
  { value: 'class_meeting', label: '班会' }
];

const sourceColorMap = {
  praise: 'green',
  violation: 'red',
  points: 'gold',
  homework: 'blue',
  grade: 'purple',
  conversation: 'cyan',
  class_meeting: 'geekblue',
  home_visit: 'orange'
};

const homeworkStatusMap = {
  assigned: { color: 'default', text: '待提交' },
  submitted: { color: 'processing', text: '已提交' },
  graded: { color: 'success', text: '已批改' },
  overdue: { color: 'error', text: '未交/逾期' }
};

const sourceLabelMap = {
  praise: '表扬',
  violation: '违纪',
  points: '积分',
  homework: '作业',
  grade: '成绩',
  conversation: '谈心谈话',
  class_meeting: '班会记录',
  home_visit: '家访'
};

const conversationTypeMap = {
  daily: '日常谈话',
  care: '关爱谈话',
  warning: '提醒谈话',
  follow_up: '跟进谈话'
};

const followUpStatusMap = {
  pending: { color: 'orange', text: '待跟进' },
  in_progress: { color: 'processing', text: '跟进中' },
  completed: { color: 'success', text: '已完成' }
};

const meetingSyncScopeOptions = [
  { value: 'current_student', label: '当前学生' },
  { value: 'specific_students', label: '指定学生' },
  { value: 'all_students', label: '全班学生' }
];

const meetingSyncScopeMap = {
  none: '仅保留班会记录',
  current_student: '同步到当前学生',
  specific_students: '同步到指定学生',
  all_students: '同步到全班学生'
};

function listPayload(data) {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
}

function extractClassList(data) {
  const payload = data?.data ?? data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.classes)) return payload.classes;
  if (Array.isArray(payload.classList)) return payload.classList;
  return [];
}

function formatClassOptionLabel(item = {}) {
  const name = item.name || item.class_name || item.className || '';
  const classCode = item.class_code || item.classCode || '';
  const id = item.id || '';
  const extra = [classCode, id].filter(Boolean).join(' / ');
  return extra ? `${name} (${extra})` : name;
}

function isGlobalAdmin(user) {
  return Boolean(user && (user.role === 'super_admin' || user.role === 'admin' || user.username === 'admin'));
}

function isTeacherAccount(user) {
  return Boolean(user && (user.role === 'teacher' || user.userType === 'teacher'));
}

function renderTagList(items = [], color = 'default') {
  if (!items.length) return null;

  return (
    <Space wrap size={[0, 8]}>
      {items.map((item) => (
        <Tag key={item} color={color}>{item}</Tag>
      ))}
    </Space>
  );
}

function uploadProps(setter) {
  return {
    multiple: true,
    beforeUpload: () => false,
    onChange: ({ fileList }) => setter(fileList)
  };
}

const StudentGrowthRecord = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [baseLoading, setBaseLoading] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [conversationOpen, setConversationOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [conversationFiles, setConversationFiles] = useState([]);
  const [meetingFiles, setMeetingFiles] = useState([]);
  const [submitting, setSubmitting] = useState('');
  const [conversationForm] = Form.useForm();
  const [meetingForm] = Form.useForm();
  const meetingClassId = Form.useWatch('class_id', meetingForm);
  const meetingSyncToGrowth = Form.useWatch('sync_to_growth', meetingForm);
  const meetingSyncScope = Form.useWatch('sync_scope', meetingForm);
  const isTeacherUser = isTeacherAccount(user);
  const isAdminUser = isGlobalAdmin(user);
  const classScopeLabel = isTeacherUser ? '我的班级' : (isAdminUser ? '全量班级' : '班级');

  useEffect(() => {
    const loadClasses = async () => {
      setBaseLoading(true);
      try {
        let classesRes;
        if (isTeacherUser) {
          classesRes = await api.get('/teacher-class-management/my-classes');
        } else {
          classesRes = await api.get('/classes', {
            params: {
              all: 'true',
              limit: 5000
            }
          });
        }

        const classList = extractClassList(classesRes.data);
        setClasses(classList);
        if (classList.length > 0) {
          setSelectedClassId((current) => (
            classList.some((item) => String(item.id) === String(current))
              ? current
              : (classList[0].id || null)
          ));
        } else {
          setSelectedClassId(null);
          setSelectedStudentId(null);
          setOverview(null);
        }
      } catch (error) {
        message.error('加载班级基础数据失败');
      } finally {
        setBaseLoading(false);
      }
    };
    loadClasses();
  }, [isTeacherUser, isAdminUser]);

  useEffect(() => {
    const loadClassStudents = async () => {
      if (!selectedClassId) {
        setStudents([]);
        return;
      }
      setBaseLoading(true);
      try {
        const studentsRes = await api.get('/students', { params: { classId: selectedClassId } });
        setStudents(listPayload(studentsRes.data));
      } catch (error) {
        message.error('加载学生数据失败');
      } finally {
        setBaseLoading(false);
      }
    };
    loadClassStudents();
  }, [selectedClassId]);

  useEffect(() => {
    if (!selectedStudentId) {
      setOverview(null);
      return;
    }
    const loadOverview = async () => {
      setLoading(true);
      try {
        const response = await studentAPI.growth.getOverview(selectedStudentId);
        setOverview(response.data?.data || null);
      } catch (error) {
        message.error(error.response?.data?.message || '获取学生成长档案失败');
      } finally {
        setLoading(false);
      }
    };
    loadOverview();
  }, [selectedStudentId]);

  const selectedStudent = overview?.student || students.find((item) => String(item.id) === String(selectedStudentId));
  const timeline = (overview?.timeline || []).filter((item) => timelineFilter === 'all' || item.source === timelineFilter);
  const classStudents = students.filter((item) => String(item.class_id) === String(meetingClassId));
  const classStudentOptions = classStudents.map((item) => ({
    value: item.id,
    label: `${item.student_name}${item.student_id ? ` / ${item.student_id}` : ''}`
  }));

  useEffect(() => {
    if (!meetingOpen) return;

    if (!meetingSyncToGrowth) {
      meetingForm.setFieldValue('student_ids', []);
      return;
    }

    if (meetingSyncScope === 'current_student') {
      if (selectedStudent && String(meetingClassId) === String(selectedStudent.class_id)) {
        meetingForm.setFieldValue('student_ids', [selectedStudent.id]);
      } else {
        meetingForm.setFieldValue('student_ids', []);
      }
      return;
    }

    if (meetingSyncScope !== 'specific_students') {
      meetingForm.setFieldValue('student_ids', []);
    }
  }, [meetingClassId, meetingForm, meetingOpen, meetingSyncScope, meetingSyncToGrowth, selectedStudent]);

  const reloadOverview = async () => {
    if (!selectedStudentId) return;
    setLoading(true);
    try {
      const response = await studentAPI.growth.getOverview(selectedStudentId);
      setOverview(response.data?.data || null);
    } catch (error) {
      message.error(error.response?.data?.message || '获取学生成长档案失败');
    } finally {
      setLoading(false);
    }
  };

  const openConversation = () => {
    if (!selectedStudent) return message.warning('请先选择学生');
    conversationForm.setFieldsValue({ title: `${selectedStudent.student_name}谈心谈话`, conversation_type: 'daily', conversation_date: dayjs(), follow_up_status: 'pending', materials: [] });
    setConversationOpen(true);
  };

  const openMeeting = () => {
    if (!selectedStudent) return message.warning('请先选择学生');
    meetingForm.setFieldsValue({
      class_id: selectedStudent.class_id,
      meeting_date: dayjs(),
      sync_to_growth: true,
      sync_scope: 'current_student',
      student_ids: [selectedStudent.id],
      materials: []
    });
    setMeetingOpen(true);
  };

  const submitConversation = async () => {
    const values = await conversationForm.validateFields();
    setSubmitting('conversation');
    try {
      await studentAPI.growth.createConversation({
        student_id: selectedStudent.id,
        title: values.title,
        conversation_type: values.conversation_type,
        location: values.location,
        participants: values.participants,
        content: values.content,
        materials: values.materials || [],
        follow_up_plan: values.follow_up_plan,
        follow_up_status: values.follow_up_status,
        conversation_date: values.conversation_date.format('YYYY-MM-DD HH:mm:ss'),
        next_follow_up_at: values.next_follow_up_at ? values.next_follow_up_at.format('YYYY-MM-DD HH:mm:ss') : '',
        attachments: conversationFiles
      });
      message.success('谈心谈话记录已保存');
      setConversationOpen(false);
      setConversationFiles([]);
      conversationForm.resetFields();
      reloadOverview();
    } catch (error) {
      if (!error?.errorFields) message.error(error.response?.data?.message || '保存谈心谈话记录失败');
    } finally {
      setSubmitting('');
    }
  };

  const submitMeetingV2 = async () => {
    const values = await meetingForm.validateFields();
    setSubmitting('meeting');
    try {
      const syncScope = values.sync_to_growth ? (values.sync_scope || 'current_student') : 'none';
      let targetStudentIds = [];

      if (values.sync_to_growth) {
        if (syncScope === 'current_student') {
          if (String(values.class_id) !== String(selectedStudent.class_id)) {
            message.warning('当前学生不在所选班级，请改为指定学生或全班同步');
            setSubmitting('');
            return;
          }
          targetStudentIds = [selectedStudent.id];
        } else if (syncScope === 'specific_students') {
          targetStudentIds = values.student_ids || [];
          if (!targetStudentIds.length) {
            message.warning('请选择要同步的学生');
            setSubmitting('');
            return;
          }
        }
      }

      const response = await studentAPI.growth.createClassMeeting({
        class_id: values.class_id,
        topic: values.topic,
        meeting_date: values.meeting_date.format('YYYY-MM-DD HH:mm:ss'),
        summary: values.summary,
        materials: values.materials || [],
        follow_up_actions: values.follow_up_actions,
        sync_to_growth: values.sync_to_growth,
        sync_scope: syncScope,
        student_ids: targetStudentIds,
        attachments: meetingFiles
      });

      message.success(response.data?.message || (values.sync_to_growth ? '班会记录已同步到学生档案' : '班会记录已保存'));
      setMeetingOpen(false);
      setMeetingFiles([]);
      meetingForm.resetFields();
      reloadOverview();
    } catch (error) {
      if (!error?.errorFields) message.error(error.response?.data?.message || '保存班会记录失败');
    } finally {
      setSubmitting('');
    }
  };

  return (
    <div className="growth-record-v2-page">
      <Card>
        <Space wrap className="growth-toolbar">
          <Select
            showSearch
            allowClear
            style={{ minWidth: 200 }}
            placeholder={classScopeLabel}
            optionFilterProp="label"
            filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
            value={selectedClassId}
            onChange={(value) => {
              setSelectedClassId(value);
              setSelectedStudentId(null);
            }}
            options={classes.map((item) => ({ value: item.id, label: formatClassOptionLabel(item) }))}
          />
          <Select
            showSearch
            allowClear
            style={{ minWidth: 320 }}
            placeholder="搜索或选择学生"
            optionFilterProp="label"
            filterOption={(input, option) => String(option?.label || '').toLowerCase().includes(input.toLowerCase())}
            value={selectedStudentId}
            loading={baseLoading}
            onChange={setSelectedStudentId}
            options={students.map((student) => ({ value: student.id, label: `${student.student_name} - ${student.class_name || '未分班'}` }))}
            disabled={!selectedClassId}
          />
          <Button icon={<ReloadOutlined />} onClick={reloadOverview} disabled={!selectedStudentId} loading={loading}>刷新档案</Button>
          <Button type="primary" icon={<SmileOutlined />} onClick={() => navigate('/students/praise')} disabled={!selectedStudent}>去表扬管理</Button>
          <Button icon={<ExclamationCircleOutlined />} onClick={() => navigate('/students/violations')} disabled={!selectedStudent}>去违纪管理</Button>
          <Button icon={<TrophyOutlined />} onClick={() => navigate('/students/points-new')} disabled={!selectedStudent}>去积分管理</Button>
          <Button icon={<MessageOutlined />} onClick={openConversation} disabled={!selectedStudent}>谈心谈话</Button>
          <Button icon={<CalendarOutlined />} onClick={openMeeting} disabled={!selectedStudent}>班会记录</Button>
        </Space>
      </Card>

      {!selectedStudentId ? (
        <Card className="growth-empty-card">
          <Empty description="先选择学生，再查看完整学生数据链" />
        </Card>
      ) : (
        <Spin spinning={loading}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
              <Row gutter={[24, 24]}>
                <Col xs={24} lg={14}>
                  <Title level={4} style={{ marginTop: 0 }}>{selectedStudent?.student_name || '学生成长档案'}</Title>
                  <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="班级">{selectedStudent?.class_name || '未分班'}</Descriptions.Item>
                    <Descriptions.Item label="学号">{selectedStudent?.student_id || '-'}</Descriptions.Item>
                    <Descriptions.Item label="座位号">{selectedStudent?.seat_number || '-'}</Descriptions.Item>
                    <Descriptions.Item label="学校">{selectedStudent?.school_name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="监护人">{selectedStudent?.parent1_name || '-'}</Descriptions.Item>
                    <Descriptions.Item label="联系电话">{selectedStudent?.parent1_phone || '-'}</Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={24} lg={10}>
                  <Alert
                    type="info"
                    showIcon
                    message="学生成长记录已经升级为完整数据链入口"
                    description="这里直接聚合学生资料、表扬、违纪、积分、作业、成绩、家访等现有数据，并新增谈心谈话和班会同步，不重复造轮子。"
                  />
                </Col>
              </Row>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="综合积分" value={overview?.summary?.total_points || 0} prefix={<TrophyOutlined />} /></Card></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="表扬记录" value={overview?.summary?.praise_count || 0} prefix={<SmileOutlined />} /></Card></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="违纪记录" value={overview?.summary?.violation_count || 0} prefix={<ExclamationCircleOutlined />} /></Card></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="谈话记录" value={overview?.summary?.conversation_count || 0} prefix={<MessageOutlined />} /></Card></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="已交作业" value={overview?.summary?.homework_submitted_count || 0} /></Card></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="待交作业" value={overview?.summary?.homework_pending_count || 0} /></Card></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="逾期作业" value={overview?.summary?.homework_overdue_count || 0} /></Card></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Card><Statistic title="平均成绩" value={overview?.summary?.average_grade || 0} precision={1} /></Card></Col>
            </Row>

            <Tabs items={[
              {
                key: 'timeline',
                label: '成长时间轴',
                children: (
                  <Card>
                    <Space wrap style={{ marginBottom: 16 }}>
                      {timelineFilters.map((item) => (
                        <Tag key={item.value} color={timelineFilter === item.value ? 'processing' : 'default'} className="growth-filter-tag" onClick={() => setTimelineFilter(item.value)}>{item.label}</Tag>
                      ))}
                    </Space>
                    {timeline.length ? (
                      <Timeline
                        items={timeline.map((item) => ({
                          color: sourceColorMap[item.source] || 'default',
                          children: (
                            <div>
                              <Space wrap>
                                <Tag color={sourceColorMap[item.source] || 'default'}>
                                  {sourceLabelMap[item.source] || item.source}
                                </Tag>
                                {item.points_delta !== null && item.points_delta !== undefined ? (
                                  <Tag color={Number(item.points_delta) >= 0 ? 'green' : 'red'}>
                                    {Number(item.points_delta) >= 0 ? `+${item.points_delta}` : item.points_delta} 分
                                  </Tag>
                                ) : null}
                              </Space>
                              <div className="growth-item-title">{item.title}</div>
                              <Paragraph style={{ marginBottom: 4 }}>{item.description || '暂无描述'}</Paragraph>
                              <Text type="secondary">{item.event_time ? dayjs(item.event_time).format('YYYY-MM-DD HH:mm') : ''}</Text>
                            </div>
                          )
                        }))}
                      />
                    ) : <Empty description="暂无时间轴记录" />}
                  </Card>
                )
              },
              {
                key: 'records',
                label: '数据总览',
                children: (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} xl={12}>
                      <Card size="small" title="作业记录">
                        {overview?.records?.homework?.length ? (
                          <List
                            dataSource={overview.records.homework}
                            renderItem={(item) => {
                              const status = homeworkStatusMap[item.status] || { color: 'default', text: item.status || '未知' };
                              return (
                                <List.Item>
                                  <List.Item.Meta
                                    title={item.title}
                                    description={`${item.subject || '未分类学科'} · ${item.due_date ? dayjs(item.due_date).format('YYYY-MM-DD') : '-'}`}
                                  />
                                  <Tag color={status.color}>{status.text}</Tag>
                                </List.Item>
                              );
                            }}
                          />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无作业记录" />}
                      </Card>
                    </Col>
                    <Col xs={24} xl={12}>
                      <Card size="small" title="积分变化">
                        {overview?.records?.points?.length ? (
                          <List
                            dataSource={overview.records.points}
                            renderItem={(item) => {
                              const delta = item.point_type === 'subtract' || item.point_type === 'deduct'
                                ? -Math.abs(Number(item.points || 0))
                                : Number(item.points || 0);
                              return (
                                <List.Item>
                                  <List.Item.Meta title={item.reason} description={item.category || '未分类'} />
                                  <Tag color={delta >= 0 ? 'green' : 'red'}>{delta >= 0 ? `+${delta}` : delta} 分</Tag>
                                </List.Item>
                              );
                            }}
                          />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无积分记录" />}
                      </Card>
                    </Col>
                    <Col xs={24} xl={12}>
                      <Card size="small" title="表扬记录">
                        {overview?.records?.praise?.length ? (
                          <List
                            dataSource={overview.records.praise}
                            renderItem={(item) => (
                              <List.Item>
                                <List.Item.Meta title={item.title} description={item.description} />
                                <Tag color="green">+{item.points_awarded || 0} 分</Tag>
                              </List.Item>
                            )}
                          />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无表扬记录" />}
                      </Card>
                    </Col>
                    <Col xs={24} xl={12}>
                      <Card size="small" title="违纪记录">
                        {overview?.records?.violations?.length ? (
                          <List
                            dataSource={overview.records.violations}
                            renderItem={(item) => (
                              <List.Item>
                                <List.Item.Meta title={item.title} description={item.description} />
                                <Tag color="red">-{item.points_deducted || 0} 分</Tag>
                              </List.Item>
                            )}
                          />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无违纪记录" />}
                      </Card>
                    </Col>
                    <Col xs={24} xl={12}>
                      <Card size="small" title="成绩记录">
                        {overview?.records?.grades?.length ? (
                          <List
                            dataSource={overview.records.grades}
                            renderItem={(item) => (
                              <List.Item>
                                <List.Item.Meta title={item.exam_name || item.exam_type || item.subject} description={item.subject || '未分类学科'} />
                                <Tag color="purple">{item.score}/{item.full_score || 100}</Tag>
                              </List.Item>
                            )}
                          />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无成绩记录" />}
                      </Card>
                    </Col>
                    <Col xs={24} xl={12}>
                      <Card size="small" title="家访记录">
                        {overview?.records?.homeVisits?.length ? (
                          <List
                            dataSource={overview.records.homeVisits}
                            renderItem={(item) => (
                              <List.Item>
                                <List.Item.Meta title={item.purpose || '家访记录'} description={item.content} />
                              </List.Item>
                            )}
                          />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无家访记录" />}
                      </Card>
                    </Col>
                  </Row>
                )
              },
              {
                key: 'conversation',
                label: '谈心谈话',
                children: (
                  <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={openConversation}>新增谈话</Button>}>
                    {overview?.records?.conversations?.length ? (
                      <List
                        dataSource={overview.records.conversations}
                        renderItem={(item) => (
                          <List.Item
                            actions={[
                              <Popconfirm
                                key="delete"
                                title="确定删除这条谈话记录吗？"
                                onConfirm={() => studentAPI.growth.deleteConversation(item.id).then(() => { message.success('谈话记录已删除'); reloadOverview(); }).catch((error) => message.error(error.response?.data?.message || '删除失败'))}
                              >
                                <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
                              </Popconfirm>
                            ]}
                          >
                            <List.Item.Meta
                              title={<Space wrap><span>{item.title}</span><Tag>{conversationTypeMap[item.conversation_type] || item.conversation_type}</Tag><Tag color="orange">{followUpStatusMap[item.follow_up_status]?.text || '待跟进'}</Tag></Space>}
                              description={<><Paragraph style={{ marginBottom: 4 }}>{item.content}</Paragraph><Text type="secondary">{item.conversation_date ? dayjs(item.conversation_date).format('YYYY-MM-DD HH:mm') : ''}</Text></>}
                            />
                          </List.Item>
                        )}
                      />
                    ) : <Empty description="暂无谈话记录" />}
                  </Card>
                )
              },
              {
                key: 'meeting',
                label: '班会记录',
                children: (
                  <Card extra={<Button icon={<PlusOutlined />} onClick={openMeeting}>新增班会</Button>}>
                    {overview?.records?.classMeetings?.length ? (
                      <List
                        dataSource={overview.records.classMeetings}
                        renderItem={(item) => (
                          <List.Item
                            actions={[
                              <Popconfirm
                                key="delete"
                                title="确定删除这条班会记录吗？"
                                onConfirm={() => studentAPI.growth.deleteClassMeeting(item.id).then(() => { message.success('班会记录已删除'); reloadOverview(); }).catch((error) => message.error(error.response?.data?.message || '删除失败'))}
                              >
                                <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
                              </Popconfirm>
                            ]}
                          >
                            <List.Item.Meta
                              title={<Space wrap><span>{item.topic}</span><Tag color="geekblue">班会</Tag></Space>}
                              description={<><Paragraph style={{ marginBottom: 4 }}>{item.summary}</Paragraph><Text type="secondary">{item.meeting_date ? dayjs(item.meeting_date).format('YYYY-MM-DD HH:mm') : ''}</Text></>}
                            />
                          </List.Item>
                        )}
                      />
                    ) : <Empty description="当前学生暂无班会同步记录" />}
                  </Card>
                )
              }
            ]} />

            <Row gutter={[16, 16]}>
              <Col xs={24} xl={12}>
                <Card title="谈话跟进看板" size="small">
                  {overview?.records?.conversations?.length ? (
                    <List
                      dataSource={overview.records.conversations.slice(0, 5)}
                      renderItem={(item) => {
                        const statusConfig = followUpStatusMap[item.follow_up_status] || followUpStatusMap.pending;
                        return (
                          <List.Item>
                            <List.Item.Meta
                              title={(
                                <Space wrap>
                                  <span>{item.title}</span>
                                  <Tag>{conversationTypeMap[item.conversation_type] || item.conversation_type}</Tag>
                                  <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                                </Space>
                              )}
                              description={(
                                <div className="growth-detail-block">
                                  <Paragraph style={{ marginBottom: 8 }}>{item.content}</Paragraph>
                                  {item.follow_up_plan ? <div className="growth-detail-row"><Text strong>后续跟进：</Text><Text>{item.follow_up_plan}</Text></div> : null}
                                  {item.next_follow_up_at ? <div className="growth-detail-row"><Text strong>下次时间：</Text><Text>{dayjs(item.next_follow_up_at).format('YYYY-MM-DD HH:mm')}</Text></div> : null}
                                  {renderTagList(item.materials, 'cyan')}
                                </div>
                              )}
                            />
                          </List.Item>
                        );
                      }}
                    />
                  ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无谈话跟进数据" />}
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card title="班会同步与后续" size="small">
                  {overview?.records?.classMeetings?.length ? (
                    <List
                      dataSource={overview.records.classMeetings.slice(0, 5)}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta
                            title={(
                              <Space wrap>
                                <span>{item.topic}</span>
                                <Tag color="geekblue">班会</Tag>
                                <Tag>{meetingSyncScopeMap[item.sync_scope] || '已同步'}</Tag>
                              </Space>
                            )}
                            description={(
                              <div className="growth-detail-block">
                                <Paragraph style={{ marginBottom: 8 }}>{item.summary}</Paragraph>
                                {item.follow_up_actions ? <div className="growth-detail-row"><Text strong>后续安排：</Text><Text>{item.follow_up_actions}</Text></div> : null}
                                {item.class_name ? <div className="growth-detail-row"><Text strong>班级：</Text><Text>{item.class_name}</Text></div> : null}
                                {renderTagList(item.materials, 'geekblue')}
                              </div>
                            )}
                          />
                        </List.Item>
                      )}
                    />
                  ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无班会跟进数据" />}
                </Card>
              </Col>
            </Row>
          </Space>
        </Spin>
      )}

            <Modal
        title="新增谈心谈话"
        open={conversationOpen}
        onCancel={() => { setConversationOpen(false); setConversationFiles([]); conversationForm.resetFields(); }}
        onOk={submitConversation}
        confirmLoading={submitting === 'conversation'}
        destroyOnHidden
        width={720}
      >
        <Form form={conversationForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title" label="谈话标题" rules={[{ required: true, message: '请输入谈话标题' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="conversation_type" label="谈话类型" rules={[{ required: true, message: '请选择谈话类型' }]}>
                <Select options={conversationTypes} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="conversation_date" label="谈话时间" rules={[{ required: true, message: '请选择谈话时间' }]}>
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location" label="谈话地点">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="participants" label="参与人员">
            <Input placeholder="例如：班主任、家长、学生" />
          </Form.Item>
          <Form.Item name="content" label="谈话内容" rules={[{ required: true, message: '请输入谈话内容' }]}>
            <TextArea rows={5} />
          </Form.Item>
          <Form.Item name="materials" label="材料关键词">
            <Select mode="tags" placeholder="输入后回车" />
          </Form.Item>
          <Form.Item name="follow_up_plan" label="后续跟进">
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="follow_up_status" label="跟进状态">
                <Select options={[
                  { value: 'pending', label: '待跟进' },
                  { value: 'in_progress', label: '跟进中' },
                  { value: 'completed', label: '已完成' }
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="next_follow_up_at" label="下次跟进时间">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="附件">
            <Upload {...uploadProps(setConversationFiles)} fileList={conversationFiles}>
              <Button icon={<PlusOutlined />}>选择附件</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新增班会记录"
        open={meetingOpen}
        onCancel={() => { setMeetingOpen(false); setMeetingFiles([]); meetingForm.resetFields(); }}
        onOk={submitMeetingV2}
        confirmLoading={submitting === 'meeting'}
        destroyOnHidden
        width={720}
      >
        <Form form={meetingForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="class_id" label="班级" rules={[{ required: true, message: '请选择班级' }]}>
                <Select options={classes.map((item) => ({ value: item.id, label: item.name }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="meeting_date" label="班会时间" rules={[{ required: true, message: '请选择班会时间' }]}>
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="topic" label="班会主题" rules={[{ required: true, message: '请输入班会主题' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="summary" label="班会内容" rules={[{ required: true, message: '请输入班会内容' }]}>
            <TextArea rows={5} />
          </Form.Item>
          <Form.Item name="materials" label="材料关键词">
            <Select mode="tags" placeholder="输入后回车" />
          </Form.Item>
          <Form.Item name="follow_up_actions" label="后续安排">
            <TextArea rows={3} placeholder="记录班会后的跟进动作、责任人或复盘计划" />
          </Form.Item>
          <Form.Item name="sync_to_growth" label="同步到学生成长档案" valuePropName="checked">
            <Switch checkedChildren="同步" unCheckedChildren="仅班会" />
          </Form.Item>
          {meetingSyncToGrowth ? (
            <>
              <Form.Item name="sync_scope" label="同步范围" rules={[{ required: true, message: '请选择同步范围' }]}>
                <Select options={meetingSyncScopeOptions} />
              </Form.Item>
              {meetingSyncScope === 'specific_students' ? (
                <Form.Item
                  name="student_ids"
                  label="指定学生"
                  rules={[{ required: true, message: '请选择需要同步的学生' }]}
                >
                  <Select
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    placeholder={classStudentOptions.length ? '请选择需要同步的学生' : '当前班级暂无学生可选'}
                    options={classStudentOptions}
                  />
                </Form.Item>
              ) : null}
              <Alert
                type="info"
                showIcon
                message={`当前同步策略：${meetingSyncScopeMap[meetingSyncScope || 'current_student']}`}
                description={
                  meetingSyncScope === 'all_students'
                    ? `保存后会同步到当前班级下 ${classStudents.length} 名学生的成长档案`
                    : meetingSyncScope === 'specific_students'
                      ? '保存后只同步到已勾选学生的成长档案'
                      : '保存后会同步到当前选中的学生成长档案'
                }
                style={{ marginBottom: 16 }}
              />
            </>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="本次仅保留班会记录"
              description="关闭同步后，班会内容仍保存在班会模块中，但不会写入学生成长档案。"
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item label="附件">
            <Upload {...uploadProps(setMeetingFiles)} fileList={meetingFiles}>
              <Button icon={<PlusOutlined />}>选择附件</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>    </div>
  );
};

export default StudentGrowthRecord;


